/**
 * Connect-on-demand for a resumed session.
 *
 * A resume opens the active tab's connections eagerly and sweeps the rest open
 * in the background (`services/lazyConnect.ts`). This hook is what covers the
 * gap: the connections the sweep deliberately skipped (their connect raises the
 * single-instance password prompt) and the window in which the sweep has not
 * reached a connection yet. When the workspace lands on a tab whose connection
 * is still pending, open it.
 *
 * It deliberately hangs off `WorkspaceConnectionNeed` — the same selector that
 * decides whether the amber banner appears (`Docs/ConnectionBanner.md`). That
 * selector already resolves single- and dual-connection tabs (Schema Compare
 * and Data Compare need both sides), so "what does this tab need" is answered
 * in exactly one place.
 *
 * Scope guard: `requestLazyConnect` only ever acts on ids the restore marked
 * deferred. A connection the user disconnected by hand is NOT in that set, so
 * clicking through tabs never re-opens it behind their back — the banner's
 * Connect button stays the only way back.
 *
 * On the tab the resume RESTORED this hook is a no-op by construction: the
 * restore's eager set is that tab's full need (`resolveEagerConnectionIds`), so
 * nothing it asks for is still pending. It earns its keep when the user *moves*
 * to a tab whose connection the sweep left alone.
 */
import { useEffect } from 'react';
import type { WorkspaceConnectionNeed } from '../../workspace/connection-banner/types';
import { requestLazyConnect } from '../services/lazyConnect';

function neededConnectionIds(need: WorkspaceConnectionNeed): string[] {
  if (need.kind === 'single') return [need.ref.connectionId];
  if (need.kind === 'dual') return [need.source.connectionId, need.target.connectionId];
  return [];
}

export function useLazyConnectOnDemand(need: WorkspaceConnectionNeed): void {
  // Key on the ids themselves: re-renders from unrelated store churn must not
  // re-run the effect, and a repeat run would be a no-op anyway (the request is
  // idempotent and dedupes concurrent callers).
  const ids = neededConnectionIds(need);
  const key = ids.join('|');

  useEffect(() => {
    if (!key) return;
    for (const connectionId of key.split('|')) {
      void requestLazyConnect(connectionId);
    }
  }, [key]);
}
